import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { InputEmail } from '../InputEmail';
import { ErrorNotice } from '../common/ErrorNotice';
import { ROUTE_PATH } from '@/constants/routes';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useApiRequest } from '@/hooks/useApiRequest';
import { useAuth } from '@/hooks/useAuth';

interface StaffLoginFormData {
  email: string;
  password: string;
}

// スタッフ画面には「はじめてのお客様」からの新規利用登録の導線が無いため、
// 認証失敗の文言は新規登録の案内を含まないスタッフ向けのものに差し替える
const AUTHENTICATION_FAILED_KEY = 'auth.authentication_failed';

export const StaffLoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const { translateApiError } = useApiErrorTranslation();
  const [loginError, setLoginError] = useState<string | string[] | null>(null);
  const [loginErrorDetails, setLoginErrorDetails] = useState<
    string[] | undefined
  >(undefined);
  const { isRequesting, execute } = useApiRequest<'POST', 'StaffLogin'>(
    'POST',
    'StaffLogin',
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StaffLoginFormData>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  // 既にログイン済みの場合はスタッフページにリダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTE_PATH.STAFF_ONLY);
    }
  }, [isAuthenticated, navigate]);

  const onFormSubmit = async (data: StaffLoginFormData) => {
    setLoginError(null);
    setLoginErrorDetails(undefined);

    const result = await execute(data);

    if (result.ok) {
      // 認証成功時の処理
      const { jwt, expires_at, role } = result.data.data;

      // AuthContextにログイン情報を保存
      await login(jwt, expires_at, role);

      // スタッフページにリダイレクト
      navigate(ROUTE_PATH.STAFF_ONLY);
    } else {
      const { message, errorKey, detailMessages } = translateApiError(
        result.error,
        result.details,
      );
      const staffAuthenticationFailed = t(
        'staffLogin.errors.authenticationFailed',
      );
      const staffMessage =
        errorKey === AUTHENTICATION_FAILED_KEY
          ? staffAuthenticationFailed
          : message;
      const staffDetailMessages = detailMessages.map((detailMessage, index) =>
        result.details[index] === AUTHENTICATION_FAILED_KEY
          ? staffAuthenticationFailed
          : detailMessage,
      );
      // 認証失敗（401）は汎用タイトル「認証が必要です」を出さず、詳細メッセージのみ表示する
      const showsDetailOnly =
        errorKey === 'http.unauthorized' && staffDetailMessages.length > 0;
      setLoginError(showsDetailOnly ? staffDetailMessages : staffMessage);
      setLoginErrorDetails(showsDetailOnly ? undefined : staffDetailMessages);
    }
  };

  return (
    <div className="e__line">
      <div className="e__inner">
        <div className="e__area">
          <div className="e__login">
            <form
              className="e__form"
              onSubmit={handleSubmit(onFormSubmit)}
              noValidate
            >
              <div className="e__fields">
                <div className="e__field">
                  <InputEmail
                    id="email"
                    className="e__input"
                    placeholder={t('staffLogin.emailPlaceholder')}
                    {...register('email', {
                      required: t('staffLogin.validation.emailRequired'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('staffLogin.validation.emailInvalid'),
                      },
                    })}
                  />
                </div>
                <div className="e__field">
                  <input
                    id="password"
                    className="e__input"
                    type="password"
                    placeholder={t('staffLogin.passwordPlaceholder')}
                    {...register('password', {
                      required: t('staffLogin.validation.passwordRequired'),
                    })}
                  />
                </div>
              </div>
              <div className="e__actions">
                <button
                  className={`e__btn e__btn--login ${isRequesting ? 'is-disabled' : ''}`}
                  type="submit"
                  disabled={isRequesting}
                >
                  {isRequesting
                    ? t('staffLogin.loginButtonLoading')
                    : t('staffLogin.loginButton')}
                </button>
              </div>
            </form>
            {/* エラーメッセージ */}
            <div className="e__alerts">
              {errors.email && (
                <div className="e__alert e__alert--error">
                  {errors.email.message}
                </div>
              )}
              {errors.password && (
                <div className="e__alert e__alert--error">
                  {errors.password.message}
                </div>
              )}
              {loginError && (
                <div className="e__alert e__alert--error">
                  <ErrorNotice
                    message={loginError}
                    details={loginErrorDetails}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
